/* eslint-disable no-unsafe-optional-chaining */
import {
  useEffect,
  useContext,
  useRef,
  useMemo,
  useImperativeHandle,
  useCallback,
  useState,
} from 'react';
import {useRouter} from 'next/router';
import useCommon from '@utils/hooks/common';
import styles from './styles';
import {CHIP_TRANSITION_DURATION, PLAYER_LAYOUT} from '@constant/game';
import {AbsolutePosition, BestCombination, PlayerComponent, PokerComponent} from '@interface';
import MultiUnitStatistic from '@components/game-room/MultiUnitStatistic';
import {Statistic} from 'antd';
import classNames from 'classnames';
import useRequest from '@utils/hooks/useRequestCustom';
import emojiApi from '@utils/api/emoji.api';
import {PositionContext} from '@utils/context';
import {debounce, getSafe} from '@utils/index';
import React from 'react';
import {JoinedRoomProps} from 'poker-interfaces/game-room/game-room-interface';
import {GameLogicInterface, GameRoomInterface} from 'poker-interfaces';
import {cloneDeep} from 'lodash';

import CanvasTable from '../CanvasTable';
import EmptySeat from '@components/game-room/EmptySeat/canvas';
import Player from '@components/game-room/Player';
import dayjs from 'dayjs';
import GameInfoArea from '@components/game-room/GameRoom/GameInfoArea';
import PotArea from '@components/game-room/PotArea';

// import Poker from '@components/Poker';
// import classnames from 'classnames';
interface AllInTableInterface {
  tableIndex: number;
  pot: number;
  renderOutCards: () => Array<JSX.Element>;
  renderCommunityHands: () => Array<JSX.Element>;
  playerSequence: Array<PlayerComponent>;
  playerAbsolutePosition?: Array<AbsolutePosition>;
  dealerBoxPosition?: number;
  gameMode?: GameLogicInterface.GameType;
  roomType?: GameRoomInterface.RoomType;

  isCurrentTableInView: boolean;

  isAllInMode?: boolean;
  myHands?: Array<PokerComponent>;
  myBestCombination?: BestCombination;
  isTimerHidden?: boolean;
  gameResultOnScreen?: GameLogicInterface.Result;
  actionTime?: number;
  playerInAction?: number;
  buyInCallback?: (position: number) => void;
  isSeated?: boolean;

  setActionTimerStatus?: React.Dispatch<
    React.SetStateAction<{order: number; expiredAt: dayjs.Dayjs; duration: number}>
  >;
  actionTimerStatusOrder?: number;
  isMyselfSeated?: boolean;
}

const mockPromise = async value => {
  return value;
};

const AllInTable = React.forwardRef((props: AllInTableInterface, ref): JSX.Element => {
  const {classes, theme, game, t, user} = useCommon({
    withStyles: styles,
    withTranslation: ['table_area', 'best_combination', 'common'],
    observers: ['socket', 'game', 'user'],
  });

  const {scaleUpValue, updatePlayerIconCenterPositions} = useContext(PositionContext);

  const jsonCache = useRef<{key: string; value: JSON} | {}>({});

  const tableAreaRef = useRef<HTMLDivElement>();
  const isDesktop = theme?.currentStyleFormat?.id === 'desktop';

  const {
    tableIndex,
    pot,
    renderOutCards,
    renderCommunityHands,
    playerSequence,
    playerAbsolutePosition,
    dealerBoxPosition,
    gameMode,
    roomType,
    isCurrentTableInView,

    isAllInMode,
    myHands,
    myBestCombination,
    isTimerHidden,
    playerInAction,
    actionTime,
    gameResultOnScreen,
    buyInCallback,
    isSeated,
    isMyselfSeated,

    setActionTimerStatus,
    actionTimerStatusOrder,
  } = props;

  const [loadingPosition, setLoadingPosition] = useState<number>(null);

  const player0Ref = useRef<PlayerComponent>();
  const player1Ref = useRef<PlayerComponent>();
  const player2Ref = useRef<PlayerComponent>();
  const player3Ref = useRef<PlayerComponent>();

  const seatsRef = useRef<HTMLElement[]>([]);

  const playersRefArray = useMemo(
    () => [player0Ref, player1Ref, player2Ref, player3Ref],
    [player0Ref, player1Ref, player2Ref, player3Ref]
  );

  useEffect(() => {
    setTimeout(() => {
      // Let DOM update before re-calculation
      getPlayersPositions();
    }, 500);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResizeEnd = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        getPlayersPositions();
      }, 500); // adjust delay as needed
    };

    window.addEventListener('resize', handleResizeEnd);
    return () => {
      window.removeEventListener('resize', handleResizeEnd);
    };
  }, [isDesktop, scaleUpValue, isCurrentTableInView]);

  useImperativeHandle(
    ref,
    () => ({
      collectAllChipsToPot: () => {
        return new Promise(resolve => {
          playersRefArray.forEach(player => {
            if (player?.current) {
              player?.current.moveChipToPot();
            }
          });
          setTimeout(() => {
            resolve(null);
          }, CHIP_TRANSITION_DURATION);
        });
      },
      resetPlayerTimer: (playerOrder: number, expiredAt: dayjs.Dayjs, duration: number) => {
        const uiIndex = playerSequence.findIndex(player => player && player.order === playerOrder);
        // original: if (!actionTimerStatus) {
        // if (actionTimerStatus?.order !== playerOrder) {
        setActionTimerStatus({
          expiredAt,
          duration,
          order: playerOrder,
        });
        // }
        playersRefArray[uiIndex]?.current?.resetTimer({expiredAt, duration});
      },
      unhidePlayerTimer: () => {
        playersRefArray.forEach(player => {
          if (player?.current) {
            player?.current?.unhideTimer();
          }
        });
      },
      hidePlayerTimer: () => {
        playersRefArray.forEach(player => {
          if (player?.current) {
            player?.current.hideTimer();
          }
        });
      },
      distributeCards: () => {
        playersRefArray.forEach(player => {
          if (player?.current) {
            player?.current.distributeCards();
          }
        });
      },
    }),
    [
      // movableObjRef,
      playersRefArray,
      playerSequence,
      actionTimerStatusOrder,
      setActionTimerStatus,
    ]
  );

  const displayDealerButton = (): JSX.Element => (
    <article
      className={classNames(classes.dealerButton, {
        position_0: dealerBoxPosition === 0,
        position_1: dealerBoxPosition === 1,
        position_2: dealerBoxPosition === 2,
        position_3: dealerBoxPosition === 3,
        hidden: typeof dealerBoxPosition === 'undefined',
      })}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images-mobile/game-room/Player/dealer-button-label.webp"
        alt="dealer-label"
        className={classes.dealerLabel}
      />
    </article>
  );

  const getSeatCenterPosition = (index): AbsolutePosition => {
    if (!seatsRef?.current[index]) return null;
    let position;

    if (playersRefArray?.[index]?.current) {
      position = playersRefArray?.[index]?.current?.containerElement?.getBoundingClientRect();
    } else {
      position = seatsRef?.current[index]?.getBoundingClientRect();
    }

    const {top, left, width, height} = position;
    const centerXOfPlayerIcon = left + width / 2;
    const centerYOfPlayerIcon = top + height / 2;
    return {
      x: centerXOfPlayerIcon,
      y: centerYOfPlayerIcon,
      xCenter: centerXOfPlayerIcon,
      yCenter: centerYOfPlayerIcon,
      top,
      left,
    };
  };

  const getPlayersPositions = (): void => {
    const refs = seatsRef;
    // const absolutePositionArray: AbsolutePosition[] = Array(6).fill(null);
    const playerIconCenterPositionArray = Array(6).fill(null);

    let shouldUpdateState = false;
    if (refs && refs?.current.length > 0) {
      refs.current.forEach((seatRef, index) => {
        // console.log('playerRef', seatRef);
        if (seatRef) {
          // console.log(' !!!! updating absolutePositionArray !!!!');
          // absolutePositionArray[index] = getSeatPosition(index);
          playerIconCenterPositionArray[index] = getSeatCenterPosition(index);
          shouldUpdateState = true;
        }
      });

      if (shouldUpdateState) {
        // console.log('playerIconCenterPositionArray', playerIconCenterPositionArray);
        // will update to all null for some reasons during render cycle
        updatePlayerIconCenterPositions(playerIconCenterPositionArray);
      }
    }
  };

  const tableInfo: JoinedRoomProps =
    tableIndex < user?.joinedRooms?.length ? user?.joinedRooms[tableIndex] : null;

  const isInAction = useCallback(
    (playerOrder: number) => {
      if (gameResultOnScreen) return false;
      if (playerOrder === playerInAction) return true;
      return false;
    },
    [gameResultOnScreen, playerInAction]
  );

  const isPlayerTheWinner = useCallback(
    (playerOrder: number): boolean => {
      // TODO: Side pot support
      if (!gameResultOnScreen) return false;
      const currentPlayer = playerSequence?.find(player => player?.order === playerOrder);
      return gameResultOnScreen.potWinnerIds.includes(currentPlayer?.id);
    },
    [gameResultOnScreen, playerSequence]
  );

  const getPlayersHands = useCallback(
    (playerOrder: number): Array<PokerComponent> => {
      return getSafe(() => playerSequence[playerOrder].hand, null);
    },
    [playerSequence]
  );

  const onBuyIn = useCallback(
    (sequenceOrder: number) => {
      setLoadingPosition(sequenceOrder);
      setTimeout(() => {
        setLoadingPosition(null);
      }, 2000);
      console.log('buy in at position', playerSequence[sequenceOrder]);
      if (buyInCallback) {
        buyInCallback(playerSequence[sequenceOrder].originalIndex);
      }
    },
    [buyInCallback, playerSequence]
  );

  const assignRef = useCallback(index => {
    return playersRefArray[index];
  }, []);

  const renderPlayer = (sequenceOrder: number, layout: PLAYER_LAYOUT) => {
    if (
      !playerSequence[sequenceOrder] ||
      typeof playerSequence[sequenceOrder]?.id === 'undefined'
    ) {
      return (
        <EmptySeat
          isAllIn={isAllInMode}
          position={sequenceOrder}
          isSeated={isSeated}
          onClick={() => onBuyIn(sequenceOrder)}
          loadingPosition={loadingPosition}
          isMyselfSeated={isMyselfSeated}
        />
      );
    }

    const totalPlayer = playerSequence?.filter(player => player?.id)?.length;

    return (
      <Player
        {...playerSequence[sequenceOrder]}
        myHands={myHands}
        myBestCombination={myBestCombination}
        isTimerHidden={isTimerHidden}
        actionTime={actionTime}
        isResultShowing={!!gameResultOnScreen}
        // only get players hand when there game result is reached
        hand={getPlayersHands(playerSequence[sequenceOrder].order)}
        isWinner={isPlayerTheWinner(playerSequence[sequenceOrder].order)}
        isInAction={isInAction(playerSequence[sequenceOrder].order)}
        layout={layout}
        icon={playerSequence[sequenceOrder].icon}
        ref={assignRef(sequenceOrder)}
        gameMode={gameMode}
        isAllInMode={isAllInMode}
        isAllIn={playerSequence[sequenceOrder].isAllIn}
        // roomCode={roomCode}
        outCards={null}
        winPercentage={null}
        sequenceOrder={sequenceOrder}
        {...playerSequence[sequenceOrder]}
        availableActions={null}
        noOfPlayers={totalPlayer}
        tableElement={tableAreaRef.current}
        isCurrentTableInView={isCurrentTableInView}
        roomCode={tableInfo?.roomCode}
      />
    );
  };

  return (
    <div
      className={classes.tableArea}
      ref={tableAreaRef}
      // style={{background: 'grey'}}
    >
      {displayDealerButton()}
      <GameInfoArea sb={tableInfo?.smallBlind} bb={tableInfo?.bigBlind} gameMode={gameMode} />
      <CanvasTable
        imagePath={theme.gameRoomStyles.tableImgPathAllIn}
        className={classes.neonTable}
      />
      <PotArea pot={pot} />
      <section className={classes.cardsOnTable}>{renderCommunityHands()}</section>

      {/* eslint-disable-next-line @next/next/no-img-element */}
      {/* <img className={classes.tableLogo} src="/images-mobile/game-room/table-logo-text-1.webp" alt="logo" /> */}
      <article
        ref={el => {
          seatsRef.current[3] = el;
        }}
        className={classes.topRight}
      >
        {renderPlayer(3, PLAYER_LAYOUT.ALL_IN_TOP_RIGHT)}
      </article>
      <article
        ref={el => {
          seatsRef.current[2] = el;
        }}
        className={classes.topMiddle}
      >
        {renderPlayer(2, PLAYER_LAYOUT.ALL_IN_TOP_ACTION_BOTTOM)}
      </article>
      <article
        ref={el => {
          seatsRef.current[1] = el;
        }}
        className={classes.topLeft}
      >
        {renderPlayer(1, PLAYER_LAYOUT.ALL_IN_TOP_LEFT)}
      </article>
      <article
        ref={el => {
          seatsRef.current[0] = el;
        }}
        className={classes.bottomMiddle}
      >
        {renderPlayer(0, PLAYER_LAYOUT.ALL_IN_BOTTOM_ACTION_TOP)}
      </article>
    </div>
  );
});

export default AllInTable;

AllInTable.displayName = 'AllInTable';
